import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { LibraryView } from '@/components/stream/LibraryView'
import { accountQuery, subaccountsQuery } from '@/lib/queries'
import { streamSearchSchema } from '@/lib/stream-filters'
import { libraryViewsQuery, streamConfigQuery, streamHitsQuery } from '@/lib/stream-queries'

/**
 * A client workspace's Library — the client's own stream, so no owner column. Gated on
 * `delivery:library` like the account route, but a disabled product redirects to the CLIENT Home —
 * the workspace you're in, not the account above it.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/subaccounts/$subaccountId/library')(
  {
    validateSearch: streamSearchSchema,
    loader: async ({ context, params }) => {
      const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
      if (!account.entitlements.includes(PRODUCT_ENTITLEMENTS.library)) {
        throw redirect({
          to: '/app/accounts/$accountId/subaccounts/$subaccountId',
          params: { accountId: params.accountId, subaccountId: params.subaccountId },
        })
      }
      // Started, not awaited — see the account's Library route (CLI-758).
      void context.queryClient.prefetchQuery(streamHitsQuery(params.subaccountId))
      await Promise.all([
        context.queryClient.ensureQueryData(subaccountsQuery(params.accountId)),
        context.queryClient.ensureQueryData(streamConfigQuery(params.subaccountId)),
        context.queryClient.ensureQueryData(libraryViewsQuery(params.subaccountId)),
      ])
    },
    component: ClientLibraryPage,
  },
)

function ClientLibraryPage() {
  const { accountId, subaccountId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const { data: subsData } = useSuspenseQuery(subaccountsQuery(accountId))
  const { data: account } = useSuspenseQuery(accountQuery(accountId))
  const client = subsData.subaccounts.find((sub) => sub.id === subaccountId)

  return (
    <LibraryView
      workspaceName={client?.name ?? 'This client'}
      storageKey={subaccountId}
      subaccountId={subaccountId}
      accountId={accountId}
      initialSearch={search}
      onConfigure={() =>
        void navigate({
          to: '/app/accounts/$accountId/subaccounts/$subaccountId/stream-config',
          params: { accountId, subaccountId },
        })
      }
      analyticsEnabled={account.entitlements.includes(PRODUCT_ENTITLEMENTS.analytics)}
      onClearDrill={() =>
        void navigate({
          to: '/app/accounts/$accountId/subaccounts/$subaccountId/library',
          params: { accountId, subaccountId },
          search: { ...search, drill: undefined },
          replace: true,
        })
      }
    />
  )
}
